<template>
  <div
    class="rf-table [&_th]:text-body [&_th]:text-black-2 flex h-full max-h-full flex-col [&_td]:p-4 [&_td]:align-middle [&_td_>_*]:m-auto [&_th]:px-4 [&_th]:py-2"
    data-cy="table"
    :class="{ [typeClasses[variant]]: true }"
  >
    <div class="rf-table--upper-wrapper">
      <div
        class="rf-table--pretable-wrapper flex flex-shrink-0 flex-grow-0 justify-between gap-5 overflow-x-auto"
      >
        <slot name="pretable"></slot>
      </div>
      <div class="rf-table--wrapper h-inherit overflow-x-auto rounded-tr-lg rounded-b-lg">
        <table class="bg-white-1 h-auto w-full rounded-tr-lg rounded-b-lg">
          <thead v-if="!hideHeader">
            <slot name="thead"></slot>
          </thead>
          <tbody>
            <slot name="tbody"></slot>
            <tr v-if="!tableData.totalCount && !loading">
              <td colspan="999">
                <slot v-if="$slots['no-data']" name="no-data"></slot>
                <div v-else class="flex h-32">
                  <span class="text-action-buttons text-grey-1 m-auto">
                    {{ noDataText || "There is no data." }}
                  </span>
                </div>
              </td>
            </tr>
            <template v-if="(loading || loadingMore) && rowSkeleton">
              <component
                v-bind="rowSkeletonProps"
                :is="rowSkeleton"
                v-for="row in tableData.loadMore"
                :key="row"
                data-cy="table--skeleton"
              ></component>
            </template>
          </tbody>
        </table>
      </div>
    </div>
    <div
      v-if="tableData && attrs?.onLoadMore"
      class="rf-table--footer relative mx-auto flex w-full grow-0"
      :class="{
        'mt-8': !hideTotal || (tableData.loadMore > 0 && tableData.showing > 0 && !loading),
      }"
    >
      <span
        v-if="!hideTotal"
        class="rf-table--total text-action-buttons text-grey-1 absolute top-1/2 left-0 text-center align-middle"
      >
        {{ `Showing ${+!!tableData.totalCount}-${tableData.showing} of ${tableData.totalCount}` }}
      </span>
      <span
        v-if="tableData.loadMore > 0 && tableData.showing > 0 && !loading && !loadingMore"
        ref="loadMoreButton"
        class="mx-auto"
      >
        <RfButtonNew
          type="tertiary"
          :text="`Load ${tableData.loadMore} more ${type}`"
          @click="attrs.onLoadMore()"
        />
      </span>
    </div>
  </div>
</template>

<script>
const typeClasses = {
  default: "rf-table--default",
  outlined:
    "rf-table--outlined [&_.rf-table--wrapper]:border [&_.rf-table--wrapper]:border-beige-10 [&_.rf-table--wrapper]:border-solid",
};
</script>

<script setup>
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import { onBeforeUnmount, ref, useAttrs, useTemplateRef } from "vue";
import { useIntersectionObserver } from "@vueuse/core";

const props = defineProps({
  tableData: { type: Object, default: () => null },
  type: { type: String, default: "items" },
  loading: { type: Boolean, default: false },
  loadingMore: { type: Boolean, default: false },
  hideHeader: { type: Boolean, default: false },
  hideTotal: { type: Boolean, default: false },
  noIntersection: { type: Boolean, default: false },
  rowSkeleton: { type: Object, default: null },
  rowSkeletonProps: { type: Object, default: () => ({}) },
  variant: { type: String, default: "default" },
  noDataText: { type: String, default: "" },
});

const attrs = useAttrs();
const loadMoreButton = useTemplateRef("loadMoreButton");
const stop = ref(() => {});
if (!props.noIntersection) {
  stop.value = useIntersectionObserver(
    loadMoreButton,
    ([{ isIntersecting }]) => isIntersecting && attrs.onLoadMore(),
  ).stop;
}
onBeforeUnmount(stop.value);
</script>

<style lang="scss" scoped>
.rf-table--total {
  transform: translateY(-50%);
}

tbody > :deep(tr) {
  animation: rf-row-in 180ms ease-out;
}

:deep(.rf-row-removing) {
  opacity: 0;
  transform: translateX(20px);
  transition:
    opacity 200ms ease-out,
    transform 200ms ease-out;
  pointer-events: none;
}

@keyframes rf-row-in {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
</style>
